<script generic="P, R" lang="ts" setup>
import type { ElSelect, LoadingInstance, SelectProps, } from "element-plus"
import type { OptionItem, Props, } from "./types"
import { ElLoading, selectEmits, selectProps, } from "element-plus"
import { isArray, omit, } from "lodash-es"
import { basePropsKeys, buildDefaultBaseProps, } from "./types"
import { useOptions, } from "./useOptions"

const props = defineProps({
  ...selectProps,
  ...buildDefaultBaseProps<P, R>(),
},)

const emits = defineEmits({
  ...selectEmits,
  "change-option": (_option?: OptionItem | OptionItem[],) => true,
  "paste": (_val: PasteData,) => true,
},)

interface PasteData {
  e: ClipboardEvent
  options: OptionItem[]
  text?: string
}

const modelValue = defineModel<typeof props.modelValue>()

const { loading, formatOptions, fetchOptions, } = useOptions<P, R>(props as Props<P, R>,)

const componentRef = ref<InstanceType<typeof ElSelect>>()

const loadingInstance = ref<LoadingInstance>()

watch(() => loading.value, (val,) => {
  if (val) {
    const target = componentRef.value?.$el
    loadingInstance.value = ElLoading.service({
      target,
      customClass: "api-select-loading-mask",
    },)
  } else {
    loadingInstance.value?.close()
  }
},)

// 过滤掉自定义的props，只保留EL组件需要的props
const bindProps = computed(() => {
  return omit(props, basePropsKeys, "modelValue", "loading",)
},)

function handleChange(val: SelectProps["modelValue"],) {
  const { apiConfig, } = props
  if (apiConfig?.config?.value) {
    if (isArray(val,)) {
      const options = formatOptions.value.filter(item => item.value && val.includes(item.value,),)
      emits("change-option", options,)
    } else {
      const option = formatOptions.value.find(item => item.value === val,)
      emits("change-option", option,)
    }
  }
  emits("change", val,)
}

function handlePaste(e: ClipboardEvent,) {
  const { clipboardData, } = e
  if (clipboardData) {
    const text = clipboardData.getData("text",)
    emits("paste", { e, options: unref(formatOptions,), text, },)
  }
}

onMounted(() => {
  componentRef.value?.inputRef?.addEventListener("paste", handlePaste,)
},)

onBeforeUnmount(() => {
  componentRef.value?.inputRef?.removeEventListener("paste", handlePaste,)
},)

defineExpose({
  fetchOptions,
  formatOptions,
  componentRef,
},)
</script>

<template>
  <ElSelect
    ref="componentRef"
    v-model="modelValue"
    :loading="loading"
    v-bind="bindProps"
    @blur="emits('blur', $event)"
    @change="handleChange"
    @clear="emits('clear')"
    @focus="emits('focus', $event)"
    @popup-scroll="emits('popup-scroll', $event)"
    @remove-tag="emits('remove-tag', $event)"
    @visible-change="emits('visible-change', $event)"
  >
    <template v-for="(_, name) in $slots" #[name]="slotData">
      <slot :name="name" v-bind="slotData" />
    </template>
    <ElOption
      v-for="(item, index) in formatOptions"
      :key="item.value"
      :disabled="item.disabled"
      :label="item.label"
      :value="item.value!"
    >
      <slot name="option" v-bind="{ option: item, options: formatOptions, index }" />
    </ElOption>
  </ElSelect>
</template>
